import { useState, useEffect, useMemo } from 'react'
import { apiClient } from '../../services/api'
import { Product } from '../../types/api'

interface StockMovement {
  id: string
  product_id: string
  product_name: string
  sku?: string
  movement_type: 'IN' | 'OUT' | 'ADJUSTMENT' | 'TRANSFER'
  quantity: number
  previous_quantity: number
  new_quantity: number
  reference_type: string
  notes?: string
  created_by_name: string
  created_at: string
}

interface StockAdjustmentData {
  product_id: string
  adjustment_type: 'SET' | 'ADD' | 'SUBTRACT'
  quantity: string
  notes: string
}

export const StockManagement = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [movements, setMovements] = useState<StockMovement[]>([])
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'adjustments' | 'movements' | 'alerts'>('overview')

  // Pagination State
  const [productsPagination, setProductsPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })
  const [loadingMore, setLoadingMore] = useState(false)

  const [adjustmentData, setAdjustmentData] = useState<StockAdjustmentData>({
    product_id: '',
    adjustment_type: 'SET',
    quantity: '',
    notes: ''
  })

  // Movement filters
  const [movementFilters, setMovementFilters] = useState({
    productId: '',
    movementType: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [productsResponse, movementsResponse, lowStockResponse] = await Promise.all([
        apiClient.getProducts({ limit: 50, offset: 0 }),
        apiClient.getStockMovements(),
        apiClient.getLowStockProducts()
      ])

      const productsData = productsResponse.products || productsResponse.data?.products || []
      const pagination = productsResponse.pagination || { total: productsData.length, limit: 50, offset: 0 }

      setProducts(productsData)
      setProductsPagination({
        total: pagination.total,
        limit: pagination.limit,
        offset: pagination.offset,
        hasMore: (pagination.offset + productsData.length) < pagination.total
      })
      setMovements(movementsResponse.movements || movementsResponse.data?.movements || [])
      setLowStockProducts(lowStockResponse.products || lowStockResponse.data?.products || [])
      setError(null)
    } catch (err) {
      console.error('Failed to load stock data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load stock data')
      setProducts([])
      setMovements([])
      setLowStockProducts([])
    } finally {
      setLoading(false)
    }
  }

  const loadMoreProducts = async () => {
    if (loadingMore || !productsPagination.hasMore) return

    try {
      setLoadingMore(true)
      const newOffset = productsPagination.offset + productsPagination.limit

      const productsResponse = await apiClient.getProducts({
        limit: 50,
        offset: newOffset
      })

      const newProducts = productsResponse.products || productsResponse.data?.products || []
      const pagination = productsResponse.pagination || { total: 0, limit: 50, offset: newOffset }

      setProducts(prev => [...prev, ...newProducts])
      setProductsPagination({
        total: pagination.total,
        limit: pagination.limit,
        offset: newOffset,
        hasMore: (newOffset + newProducts.length) < pagination.total
      })
    } catch (err) {
      console.error('Failed to load more products:', err)
      setError(err instanceof Error ? err.message : 'Failed to load more products')
    } finally {
      setLoadingMore(false)
    }
  }

  const handleStockAdjustment = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (!selectedProduct) {
        setError('No product selected')
        return
      }

      // Calculate new_quantity based on adjustment_type
      const currentStock = parseFloat(selectedProduct.stock_quantity || '0')
      const adjustmentQty = parseFloat(adjustmentData.quantity || '0')
      let newQuantity = 0

      switch (adjustmentData.adjustment_type) {
        case 'SET':
          newQuantity = adjustmentQty
          break
        case 'ADD':
          newQuantity = currentStock + adjustmentQty
          break
        case 'SUBTRACT':
          newQuantity = Math.max(0, currentStock - adjustmentQty)
          break
        default:
          newQuantity = adjustmentQty
      }

      // Send the calculated new_quantity to the API
      await apiClient.adjustStock({
        product_id: adjustmentData.product_id,
        new_quantity: newQuantity,
        notes: adjustmentData.notes || `${adjustmentData.adjustment_type}: ${adjustmentQty}`
      })

      await loadData()
      setShowAdjustmentForm(false)
      setAdjustmentData({
        product_id: '',
        adjustment_type: 'SET',
        quantity: '',
        notes: ''
      })
      setSelectedProduct(null)
    } catch (err) {
      console.error('Stock adjustment error:', err)
      setError(err instanceof Error ? err.message : 'Failed to adjust stock')
    }
  }

  const openAdjustmentForm = (product: Product) => {
    setSelectedProduct(product)
    setAdjustmentData({
      product_id: product.id,
      adjustment_type: 'SET',
      quantity: String(product.stock_quantity || 0),
      notes: ''
    })
    setShowAdjustmentForm(true)
  }

  const closeAdjustmentForm = () => {
    setShowAdjustmentForm(false)
    setSelectedProduct(null)
    setAdjustmentData({
      product_id: '',
      adjustment_type: 'SET',
      quantity: '',
      notes: ''
    })
  }

  const getMovementTypeColor = (type: string) => {
    switch (type) {
      case 'IN': return 'text-green-600 bg-green-100'
      case 'OUT': return 'text-red-600 bg-red-100'
      case 'ADJUSTMENT': return 'text-blue-600 bg-blue-100'
      case 'TRANSFER': return 'text-purple-600 bg-purple-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStockStatusColor = (current: number, min: number) => {
    if (current <= min) return 'text-red-600 bg-red-100'
    if (current <= min * 2) return 'text-yellow-600 bg-yellow-100'
    return 'text-green-600 bg-green-100'
  }

  // Calculate preview of new stock quantity
  const previewNewStock = useMemo(() => {
    if (!selectedProduct || !adjustmentData.quantity) return null

    const currentStock = parseFloat(selectedProduct.stock_quantity || '0')
    const adjustmentQty = parseFloat(adjustmentData.quantity || '0')

    if (isNaN(adjustmentQty)) return null

    switch (adjustmentData.adjustment_type) {
      case 'SET':
        return adjustmentQty
      case 'ADD':
        return currentStock + adjustmentQty
      case 'SUBTRACT':
        return Math.max(0, currentStock - adjustmentQty)
      default:
        return null
    }
  }, [selectedProduct, adjustmentData.adjustment_type, adjustmentData.quantity])

  // Filter movements based on filter criteria
  const filteredMovements = useMemo(() => {
    return (movements || []).filter(movement => {
      // Product filter
      if (movementFilters.productId && movement.product_id !== movementFilters.productId) {
        return false
      }

      // Movement type filter
      if (movementFilters.movementType && movement.movement_type !== movementFilters.movementType) {
        return false
      }

      // Date from filter
      if (movementFilters.dateFrom && movement.created_at < movementFilters.dateFrom) {
        return false
      }

      // Date to filter
      if (movementFilters.dateTo && movement.created_at > movementFilters.dateTo + ' 23:59:59') {
        return false
      }

      // Search term filter (searches product name, SKU, notes, created by)
      if (movementFilters.searchTerm) {
        const searchLower = movementFilters.searchTerm.toLowerCase()
        return (
          movement.product_name?.toLowerCase().includes(searchLower) ||
          movement.sku?.toLowerCase().includes(searchLower) ||
          movement.notes?.toLowerCase().includes(searchLower) ||
          movement.created_by_name?.toLowerCase().includes(searchLower)
        )
      }

      return true
    })
  }, [movements, movementFilters])

  const clearFilters = () => {
    setMovementFilters({
      productId: '',
      movementType: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stock Management</h1>
          <p className="text-gray-600">Monitor and adjust inventory levels</p>
        </div>
        <button
          onClick={() => setShowAdjustmentForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md font-medium"
        >
          Adjust Stock
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', count: (products || []).length },
            { id: 'adjustments', name: 'Quick Adjustments', count: null },
            { id: 'movements', name: 'Movement History', count: (movements || []).length },
            { id: 'alerts', name: 'Low Stock Alerts', count: (lowStockProducts || []).length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
              {tab.count !== null && (
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Stock Adjustment Modal */}
      {showAdjustmentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedProduct ? `Adjust Stock: ${selectedProduct.name}` : 'Select Product'}
              </h2>
              <button onClick={closeAdjustmentForm} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>

            <form onSubmit={handleStockAdjustment} className="space-y-4">
              {!selectedProduct && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                  <select
                    required
                    value={adjustmentData.product_id}
                    onChange={(e) => {
                      const product = (products || []).find(p => p.id === e.target.value)
                      if (product) {
                        setSelectedProduct(product)
                        setAdjustmentData(prev => ({
                          ...prev,
                          product_id: e.target.value,
                          quantity: String(product.stock_quantity || 0)
                        }))
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select a product</option>
                    {(products || []).map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} (Current: {product.stock_quantity})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedProduct && (
                <>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-600">Current Stock: <span className="font-semibold">{parseFloat(selectedProduct.stock_quantity || '0')}</span></p>
                    <p className="text-sm text-gray-600">Min Level: <span className="font-semibold">{selectedProduct.min_stock_level || 10}</span></p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
                    <select
                      value={adjustmentData.adjustment_type}
                      onChange={(e) => setAdjustmentData(prev => ({ ...prev, adjustment_type: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="SET">Set to specific amount</option>
                      <option value="ADD">Add to current stock</option>
                      <option value="SUBTRACT">Subtract from current stock</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity {adjustmentData.adjustment_type === 'SET' ? '(New Total)' : '(Amount to ' + (adjustmentData.adjustment_type === 'ADD' ? 'Add' : 'Subtract') + ')'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={adjustmentData.quantity}
                      onChange={(e) => setAdjustmentData(prev => ({ ...prev, quantity: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Preview of new stock */}
                  {previewNewStock !== null && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                            New Stock Will Be
                          </p>
                          <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                            {previewNewStock.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Change</p>
                          <p className={`text-xl font-semibold ${
                            previewNewStock > parseFloat(selectedProduct.stock_quantity || '0')
                              ? 'text-green-600 dark:text-green-400'
                              : previewNewStock < parseFloat(selectedProduct.stock_quantity || '0')
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {previewNewStock > parseFloat(selectedProduct.stock_quantity || '0') && '+'}
                            {(previewNewStock - parseFloat(selectedProduct.stock_quantity || '0')).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={adjustmentData.notes}
                      onChange={(e) => setAdjustmentData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Reason for adjustment..."
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAdjustmentForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedProduct}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-md"
                >
                  Adjust Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {(products || []).map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow border border-orange-200 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800 truncate">{product.name}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                    getStockStatusColor(parseInt(product.stock_quantity), product.min_stock_level || 10)
                  }`}>
                    {parseInt(product.stock_quantity) <= (product.min_stock_level || 10) ? 'Low Stock' : 'In Stock'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Stock:</span>
                    <span className="font-semibold">{product.stock_quantity}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Min Level:</span>
                    <span className="font-semibold">{product.min_stock_level || 10}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Category:</span>
                    <span className="font-semibold">{product.category_name}</span>
                  </div>
                  {product.sku && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">SKU:</span>
                      <span className="font-semibold">{product.sku}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => openAdjustmentForm(product)}
                  className="w-full bg-orange-100 hover:bg-orange-200 text-orange-800 py-2 px-3 rounded-md text-sm font-medium transition-colors"
                >
                  Adjust Stock
                </button>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {productsPagination.hasMore && (
            <div className="flex flex-col items-center justify-center py-6">
              <p className="text-sm text-gray-600 mb-3">
                Showing {products.length} of {productsPagination.total} products
              </p>
              <button
                onClick={loadMoreProducts}
                disabled={loadingMore}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-6 py-2 rounded-md font-medium min-w-[200px]"
              >
                {loadingMore ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </span>
                ) : (
                  'Load More Products'
                )}
              </button>
            </div>
          )}

          {/* All loaded message */}
          {!productsPagination.hasMore && products.length > 0 && productsPagination.total > 50 && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">
                All {products.length} products loaded
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'adjustments' && (
        <div className="bg-white rounded-lg shadow border border-orange-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Stock Adjustments</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {(products || []).filter(p => parseInt(p.stock_quantity) <= (p.min_stock_level || 10) * 2).map((product) => (
              <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-800 truncate">{product.name}</h4>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                    getStockStatusColor(parseInt(product.stock_quantity), product.min_stock_level || 10)
                  }`}>
                    {product.stock_quantity}
                  </span>
                </div>
                <button
                  onClick={() => openAdjustmentForm(product)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-1 px-3 rounded text-sm"
                >
                  Adjust
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'movements' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow border border-orange-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Filter Movements</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Clear All Filters
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Product, SKU, Notes..."
                  value={movementFilters.searchTerm}
                  onChange={(e) => setMovementFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Product Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                <select
                  value={movementFilters.productId}
                  onChange={(e) => setMovementFilters(prev => ({ ...prev, productId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Products</option>
                  {(products || []).map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Movement Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={movementFilters.movementType}
                  onChange={(e) => setMovementFilters(prev => ({ ...prev, movementType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Types</option>
                  <option value="IN">IN</option>
                  <option value="OUT">OUT</option>
                  <option value="ADJUSTMENT">ADJUSTMENT</option>
                  <option value="TRANSFER">TRANSFER</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={movementFilters.dateFrom}
                  onChange={(e) => setMovementFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={movementFilters.dateTo}
                  onChange={(e) => setMovementFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>

            {/* Results count */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold text-gray-900">{filteredMovements.length}</span> of <span className="font-semibold text-gray-900">{(movements || []).length}</span> movements
              </p>
            </div>
          </div>

          {/* Movements Table */}
          <div className="bg-white rounded-lg shadow border border-orange-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Before → After</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMovements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{movement.product_name}</div>
                          {movement.sku && <div className="text-xs text-gray-500">SKU: {movement.sku}</div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getMovementTypeColor(movement.movement_type)}`}>
                          {movement.movement_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                        <span className={movement.quantity >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {movement.quantity >= 0 ? '+' : ''}{movement.quantity}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {movement.previous_quantity} → {movement.new_quantity}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {movement.notes}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(movement.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {movement.created_by_name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredMovements.length === 0 && (movements || []).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No stock movements recorded yet
                </div>
              )}
              {filteredMovements.length === 0 && (movements || []).length > 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-2">No movements match your filters</p>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {(lowStockProducts || []).length > 0 ? (
            <>
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-center">
                  <div className="text-red-600 mr-3">⚠️</div>
                  <div>
                    <h4 className="font-medium text-red-800">Low Stock Alert</h4>
                    <p className="text-sm text-red-600 mt-1">
                      {(lowStockProducts || []).length} product(s) are running low on stock and need restocking.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {(lowStockProducts || []).map((product) => (
                  <div key={product.id} className="bg-white border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-800 truncate">{product.name}</h4>
                      <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                        Critical
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Current Stock:</span>
                        <span className="font-semibold text-red-600">{product.stock_quantity}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Min Level:</span>
                        <span className="font-semibold">{product.min_stock_level || 10}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Category:</span>
                        <span className="font-semibold">{product.category_name}</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => openAdjustmentForm(product)}
                      className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-3 rounded-md text-sm font-medium transition-colors"
                    >
                      Restock Now
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">All Products Well Stocked</h3>
              <p className="text-gray-600">
                No products are currently below their minimum stock levels.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}